import moment from 'moment'
import { Fragment, ReactElement, useState } from 'react'
import Spinner from 'app/components/Spinner'
import { cn } from 'app/lib/utils'
import { hueOutline, hueTint } from 'lib/chartColors'
import type { SlippingDay, SlippingRow } from './voiceAgentOpsMetrics'

const CALLS_STYLE = { backgroundColor: hueTint(`green`, 0.6), borderColor: hueOutline(`green`) }
const NO_CALLS_STYLE = { backgroundColor: hueTint(`red`, 0.6), borderColor: hueOutline(`red`) }

/** What a cell counts, in both numbers ("AI call" / "AI calls"). */
export type SlippingActivityLabel = { one: string; other: string }

const AI_CALL_LABEL: SlippingActivityLabel = { one: `AI call`, other: `AI calls` }

function describeDay(day: SlippingDay, label: SlippingActivityLabel): string {
  if (day.calls > 0) return `${day.calls} ${day.calls === 1 ? label.one : label.other}`
  if (day.daysSinceCall === undefined) return `No ${label.other} on record`
  return `Day ${day.daysSinceCall} of ${day.gapDays} without an ${label.one}`
}

type HoverCell = { locationName: string; day: SlippingDay; x: number; y: number }

/**
 * Location × day grid, from the start of the range through today: green on days with
 * AI calls, red on days without. Same cell and tooltip treatment as `CallTimeHeatmap`.
 * `activityLabel` names what a cell counts when it is not AI calls (the SMS Ops tab counts AI replies).
 */
export function SlippingGrid({
  rows,
  isLoading,
  emptyMessage,
  activityLabel = AI_CALL_LABEL,
}: {
  rows?: SlippingRow[]
  isLoading: boolean
  emptyMessage: string
  activityLabel?: SlippingActivityLabel
}): ReactElement {
  const [hover, setHover] = useState<HoverCell | null>(null)
  const days = rows?.[0]?.days ?? []
  // Around 15 date labels whatever the range, so dense ranges stay legible.
  const labelEvery = Math.max(1, Math.ceil(days.length / 15))

  return (
    <div className="rounded-lg border border-default p-4">
      {isLoading || !rows ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size={16} />
        </div>
      ) : !rows.length || !days.length ? (
        <p className="py-10 text-center text-body-sm text-subtle">
          {rows.length ? `This date range hasn't started yet.` : emptyMessage}
        </p>
      ) : (
        <>
          <div className="max-h-[480px] overflow-auto">
            <div
              className="grid min-w-[640px] items-center gap-1"
              style={{ gridTemplateColumns: `12rem repeat(${days.length}, minmax(0, 1fr))` }}
              onMouseLeave={() => setHover(null)}
            >
              <div />
              {days.map((day, index) => (
                // A label spans about `labelEvery` columns, wider than its own day, so it sits
                // outside the layout: centered on its column, or pinned to the grid's edge when
                // fewer than half a label's columns remain on that side. Left in flow and centered,
                // the last label hung past the right edge and made the whole grid scroll.
                <div key={day.date} className="relative h-3">
                  {index % labelEvery === 0 && (
                    <span
                      className={cn(
                        `absolute top-0 whitespace-nowrap text-body-xs leading-none text-strong`,
                        index + 0.5 < labelEvery / 2
                          ? `left-0`
                          : days.length - index - 0.5 < labelEvery / 2
                            ? `right-0`
                            : `left-1/2 -translate-x-1/2`,
                      )}
                    >
                      {moment.utc(day.date).format(`MMM D`)}
                    </span>
                  )}
                </div>
              ))}
              {rows.map((row) => (
                <Fragment key={row.locationId}>
                  <div className="truncate pr-2 text-body-xs text-default" title={row.locationName}>
                    {row.locationName}
                  </div>
                  {row.days.map((day) => (
                    <div
                      key={day.date}
                      onMouseMove={(event) =>
                        setHover({ locationName: row.locationName, day, x: event.clientX, y: event.clientY })
                      }
                      className="h-6 rounded border"
                      style={day.calls > 0 ? CALLS_STYLE : NO_CALLS_STYLE}
                    />
                  ))}
                </Fragment>
              ))}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-body-xs text-default">
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded border" style={CALLS_STYLE} />
              {activityLabel.other}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded border" style={NO_CALLS_STYLE} />
              {`No ${activityLabel.other}`}
            </span>
          </div>
        </>
      )}

      {hover && (
        <div
          className="pointer-events-none fixed z-50 rounded-md bg-inverse px-2.5 py-1.5 shadow-lg"
          style={{ top: hover.y + 14, left: hover.x + 14 }}
        >
          <div className="text-ui-xs text-inverse">
            {`${hover.locationName} · ${moment.utc(hover.day.date).format(`MMM D, YYYY`)}`}
          </div>
          <div className="text-body-xs text-inverse/80">{describeDay(hover.day, activityLabel)}</div>
        </div>
      )}
    </div>
  )
}
